import {
  WorkspaceDocument,
  WorkspaceFolder,
} from "@/models/workspace/Workspace";
import Workspace from "@/models/workspace/Workspace";
import { UploadSource } from "@/components/upload/types/upload-item";
import {
  FileSyncOptions,
  IdentifiedChange,
  SyncResultVariant,
  SyncResults,
  SyncReportDocumentEntry,
  SyncReportFileEntry,
  SyncReportNewDocEntry,
  createEmptySyncResults,
} from "@/types/sync.types";
import { getDocumentFolderPath, findPathToFolder } from "../utils/folderUtils";
import { FolderMetadata, FolderMetadataMap } from "@/types/folder-metadata";
import { updateProgressToast } from "@/components/custom-toasts/toasts";
import { DownloadedFileWithIds } from "../providers/SharePointProvider";
import { User } from "@/models/users/User";
import useWorkspace from "@/hooks/useWorkspace";
import { logger } from "@/lib/logger";
import {
  downloadDriveItemContent,
  GraphCallOptions,
} from "@/services/microsoft/graphApiService";
import {
  GraphRequestError,
  describeGraphFailure,
} from "@/services/microsoft/graphFetch";

/**
 * Dependencies required by the sync mutation hook
 */
type SyncMutationDependencies = {
  workspace: Workspace;
  /** True when the current user is the workspace owner or has Admin role */
  isAdminOrOwner: boolean;
  /** All tenant users, used to resolve owner display names in the report */
  tenantUsers: User[];
  handleSharePointFileSyncClick: (
    document: WorkspaceDocument,
    options?: FileSyncOptions
  ) => Promise<{
    variant: SyncResultVariant;
    description: string;
    title: string;
  }>;
  handleFolderUpload: (
    files: FileList,
    workspace: Workspace,
    folderId?: string,
    providedMetadata?: undefined,
    skipConflictCheck?: boolean,
    skipMetadataCheck?: boolean,
    fileMetadataMap?: Map<
      string,
      {
        source: UploadSource;
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
      }
    >,
    folderMetadataMap?: Map<string, FolderMetadata>
  ) => Promise<void>;
  createFileListFromDownloadedFiles: (
    filesWithIds: DownloadedFileWithIds[]
  ) => {
    fileList: FileList;
    metadataMap: Map<string, DownloadedFileWithIds>;
  };
  /** ID of the user who initiated the sync — used to resolve the owner name for newly added documents */
  currentUserId?: string;
};

/**
 * Options for executing sync mutations
 */
type ExecuteSyncOptions = {
  progressToastId: string | number;
  parentFolderId?: string;
  /** Shared by every Graph call of this sync (new-file downloads). */
  graphOptions?: GraphCallOptions;
};

/**
 * Return type for the sync mutation hook
 */
type SyncMutationReturn = {
  executeSyncMutations:
    | ((
        changes: IdentifiedChange,
        options: ExecuteSyncOptions
      ) => Promise<SyncResults>)
    | null;
  buildSyncReport: (results: SyncResults) => string[];
  hasErrors: (results: SyncResults) => boolean;
};

/**
 * Hook that provides sync mutation functionality
 */
export const useSyncMutation = (
  deps: SyncMutationDependencies | null
): SyncMutationReturn => {
  const { updateFolderMetadata } = useWorkspace();

  // If no dependencies provided, return null executor
  if (!deps) {
    return {
      executeSyncMutations: null,
      buildSyncReport: () => [],
      hasErrors: () => false,
    };
  }

  const {
    workspace,
    isAdminOrOwner,
    tenantUsers,
    handleSharePointFileSyncClick,
    handleFolderUpload,
    createFileListFromDownloadedFiles,
    currentUserId,
  } = deps;

  /**
   * Resolves a display name for a user from the tenant users list.
   * Falls back to email, then to undefined if not found.
   */
  const resolveOwnerName = (userId: string): string | undefined => {
    const user = tenantUsers.find((u) => u.id === userId);
    if (!user) return undefined;
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return fullName || user.email;
  };

  /**
   * Syncs existing documents from SharePoint/OneDrive
   */
  const syncExistingDocuments = async (
    documents: WorkspaceDocument[],
    progressToastId: string | number,
    results: SyncResults,
    graphOptions: GraphCallOptions,
    listedFileModified: Map<string, number>
  ): Promise<void> => {
    if (documents.length === 0) return;

    updateProgressToast(
      progressToastId,
      `Synkroniserer ${documents.length} eksisterende fil${
        documents.length === 1 ? "" : "er"
      }...`,
      "Synkroniserer eksisterende filer"
    );

    for (let i = 0; i < documents.length; i++) {
      const doc = documents[i];

      updateProgressToast(
        progressToastId,
        `Synkroniserer fil: ${doc.name} (${i + 1}/${documents.length})`,
        "Synkroniserer eksisterende filer"
      );

      try {
        const result = await handleSharePointFileSyncClick(doc, {
          graphOptions,
          cloudLastModified: doc.sharepointFileId
            ? listedFileModified.get(doc.sharepointFileId)
            : undefined,
        });
        const folderPath = getDocumentFolderPath(workspace, doc.id);
        const fileEntry: SyncReportFileEntry = {
          document: doc,
          folderPath,
          ownerName: resolveOwnerName(doc.userId),
        };

        if (result.variant === SyncResultVariant.MISSING) {
          // File couldn't be accessed during download — treat like an
          // inaccessible cloud file so admin/owner can decide to delete.
          const entry: SyncReportDocumentEntry = {
            document: doc,
            folderPath,
            ownerName: resolveOwnerName(doc.userId),
            status: "inaccessible",
          };
          if (isAdminOrOwner) {
            results.missingFilesForAdminReview.push(entry);
          } else {
            results.skippedMissingDocuments.push(entry);
          }
          results.existingDocumentsFailed++;
        } else if (
          result.variant === SyncResultVariant.DESTRUCTIVE ||
          result.variant === SyncResultVariant.FAILED
        ) {
          // FAILED: SharePoint/OneDrive didn't answer. The file may be fine,
          // so it never goes to the missing-files review.
          results.existingDocumentsFailed++;
          results.errors.push(`${doc.name}: ${result.description}`);
        } else if (result.title === "Ingen opdatering nødvendig") {
          results.existingDocumentsNoUpdate++;
          results.noUpdateDocuments.push(fileEntry);
        } else {
          results.existingDocumentsSynced++;
          results.syncedDocuments.push(fileEntry);
        }
      } catch (error) {
        results.existingDocumentsFailed++;
        results.errors.push(
          `${doc.name}: ${
            error instanceof Error ? error.message : "Ukendt fejl"
          }`
        );
      }
    }
  };

  /**
   * Updates folder names that have changed in SharePoint/OneDrive
   */
  const updateFolderNames = async (
    folderNameUpdates: Array<{ folder: WorkspaceFolder; newName: string }>,
    progressToastId: string | number,
    results: SyncResults
  ): Promise<void> => {
    if (folderNameUpdates.length === 0) return;

    updateProgressToast(
      progressToastId,
      `Opdaterer ${folderNameUpdates.length} mappenavn${
        folderNameUpdates.length === 1 ? "" : "e"
      }...`,
      "Opdaterer mappenavne"
    );

    for (const { folder, newName } of folderNameUpdates) {
      try {
        await updateFolderMetadata.mutateAsync({
          workspaceId: workspace.id,
          folderId: folder.id,
          requestBody: {
            folderSource: folder.folderSource,
            driveId: folder.driveId,
            siteId: folder.siteId,
            sharepointFileId: folder.sharepointFileId,
            name: newName,
          },
        });
        results.folderNamesUpdated++;
      } catch (error) {
        results.errors.push(
          `Mappe ${folder.name}: ${
            error instanceof Error ? error.message : "Ukendt fejl"
          }`
        );
      }
    }
  };

  /**
   * Creates new folders detected in SharePoint/OneDrive (including empty folders)
   */
  const addNewFolders = async (
    changes: IdentifiedChange,
    progressToastId: string | number,
    results: SyncResults
  ): Promise<void> => {
    if (changes.newFolders.length === 0) return;

    updateProgressToast(
      progressToastId,
      `Opretter ${changes.newFolders.length} ny${
        changes.newFolders.length === 1 ? " mappe" : "e mapper"
      }...`,
      "Opretter nye mapper"
    );

    const foldersByParent = new Map<string | undefined, IdentifiedChange["newFolders"]>();
    for (const folder of changes.newFolders) {
      const existing = foldersByParent.get(folder.parentFolderId) || [];
      existing.push(folder);
      foldersByParent.set(folder.parentFolderId, existing);
    }

    for (const [parentFolderId, newFolders] of foldersByParent.entries()) {
      try {
        const folderMetadataMap: FolderMetadataMap = new Map();
        for (const folder of newFolders) {
          folderMetadataMap.set(folder.folderPath, {
            source: folder.metadata.source,
            driveId: folder.metadata.driveId,
            siteId: folder.metadata.siteId,
            sharepointFileId: folder.metadata.sharepointFileId,
          });
        }

        // Empty FileList (folder creation is driven by folderMetadataMap)
        const emptyFileList = new DataTransfer().files;

        await handleFolderUpload(
          emptyFileList,
          workspace,
          parentFolderId,
          undefined,
          true, // skip conflict check (no files involved)
          true, // skip metadata check (no files involved)
          undefined,
          folderMetadataMap
        );

        results.newFoldersCreated += newFolders.length;
      } catch (error) {
        results.errors.push(
          `Oprettelse af mapper: ${
            error instanceof Error ? error.message : "Ukendt fejl"
          }`
        );
      }
    }
  };

  /**
   * Adds new documents detected in SharePoint/OneDrive
   */
  const addNewDocuments = async (
    changes: IdentifiedChange,
    progressToastId: string | number,
    parentFolderId: string | undefined,
    results: SyncResults,
    graphOptions: GraphCallOptions
  ): Promise<void> => {
    if (changes.newDocuments.length === 0) return;

    updateProgressToast(
      progressToastId,
      `Tilføjer ${changes.newDocuments.length} ny${
        changes.newDocuments.length === 1 ? " fil" : "e filer"
      }...`,
      "Tilføjer nye filer"
    );

    try {
      const docsByParent = new Map<string | undefined, typeof changes.newDocuments>();
      for (const doc of changes.newDocuments) {
        const key = doc.parentFolderId || parentFolderId;
        const existing = docsByParent.get(key) || [];
        existing.push(doc);
        docsByParent.set(key, existing);
      }

      for (const [targetFolderId, candidates] of docsByParent.entries()) {
        // Change identification only lists files; download the new ones now.
        const docs: typeof candidates = [];
        const filesWithIds: DownloadedFileWithIds[] = [];
        for (const doc of candidates) {
          try {
            const file = await downloadDriveItemContent(
              doc.fileMetadata,
              doc.fileName,
              graphOptions
            );
            docs.push(doc);
            filesWithIds.push({
              file,
              source: doc.fileMetadata.source,
              driveId: doc.fileMetadata.driveId,
              siteId: doc.fileMetadata.siteId,
              sharepointFileId: doc.fileMetadata.sharepointFileId,
              folderPath: doc.folderPath,
            });
          } catch (error) {
            logger.error("Failed to download a new file during sync", {
              operation: "syncDownloadNewFile",
              error,
              status:
                error instanceof GraphRequestError ? error.status : undefined,
              workspaceId: workspace.id,
              folderId: targetFolderId,
              driveId: doc.fileMetadata.driveId,
              siteId: doc.fileMetadata.siteId,
              sharepointFileId: doc.fileMetadata.sharepointFileId,
            });
            results.errors.push(
              `${doc.fileName}: kunne ikke hentes (${describeGraphFailure(
                error
              )}). Prøv igen om lidt.`
            );
          }
        }

        // Convert to FileList
        const { fileList } = createFileListFromDownloadedFiles(filesWithIds);

        // Build fileMetadataMap
        const fileMetadataMap = new Map<
          string,
          {
            source: UploadSource;
            driveId?: string;
            siteId?: string;
            sharepointFileId?: string;
          }
        >();

        docs.forEach((doc) => {
          const filePath = doc.folderPath
            ? `${doc.folderPath}/${doc.fileName}`
            : doc.fileName;
          fileMetadataMap.set(filePath, {
            source: doc.fileMetadata.source,
            driveId: doc.fileMetadata.driveId,
            siteId: doc.fileMetadata.siteId,
            sharepointFileId: doc.fileMetadata.sharepointFileId,
          });
        });

        // Build folderMetadataMap from identified new folders (ensures empty folders get created too)
        const folderMetadataMap: FolderMetadataMap = new Map();
        changes.newFolders
          .filter(
            (f) => (f.parentFolderId || parentFolderId) === targetFolderId
          )
          .forEach((f) => {
            folderMetadataMap.set(f.folderPath, {
              source: f.metadata.source,
              driveId: f.metadata.driveId,
              siteId: f.metadata.siteId,
              sharepointFileId: f.metadata.sharepointFileId,
            });
          });

        // Every download failed and there are no folders to create either.
        if (docs.length === 0 && folderMetadataMap.size === 0) continue;

        await handleFolderUpload(
          fileList,
          workspace,
          targetFolderId,
          undefined,
          true,
          true,
          fileMetadataMap,
          folderMetadataMap.size > 0 ? folderMetadataMap : undefined
        );

        // Count folders created as part of this folder+file upload operation (includes empty folders)
        results.newFoldersCreated += folderMetadataMap.size;
        results.newDocumentsAdded += docs.length;

        // Track per-file new document entries for CSV export.
        // Lara document IDs aren't available here — handleFolderUpload returns void.
        // The SharePoint file ID is used as proxy. Owner is the sync initiator.
        //
        // doc.folderPath is RELATIVE to the synced Lara folder (targetFolderId),
        // e.g. "" for root, "SubFolder/Nested" for deeper files.
        // We resolve the full workspace path by prepending the Lara folder path.
        const targetFolderPath = findPathToFolder(workspace.folders, targetFolderId ?? "")
          ?.map((p) => p.name)
          .join("/") ?? "";

        const initiatorName = currentUserId
          ? resolveOwnerName(currentUserId)
          : undefined;

        for (const doc of docs) {
          const fullPath = doc.folderPath
            ? targetFolderPath
              ? `${targetFolderPath}/${doc.folderPath}`
              : doc.folderPath
            : targetFolderPath;

          const entry: SyncReportNewDocEntry = {
            fileName: doc.fileName,
            folderPath: fullPath,
            sharepointFileId: doc.fileMetadata.sharepointFileId,
            ownerName: initiatorName,
          };
          results.newDocumentEntries.push(entry);
        }
      }
    } catch (error) {
      results.errors.push(
        `Tilføjelse af nye filer: ${
          error instanceof Error ? error.message : "Ukendt fejl"
        }`
      );
    }
  };

  /**
   * Executes all sync mutations for the given changes
   */
  const executeSyncMutations = async (
    changes: IdentifiedChange,
    options: ExecuteSyncOptions
  ): Promise<SyncResults> => {
    const results = createEmptySyncResults();
    const { progressToastId, parentFolderId, graphOptions = {} } = options;

    // Folders whose contents couldn't be read during identification.
    results.errors.push(...changes.errors);

    // Step 1: Sync existing documents
    await syncExistingDocuments(
      changes.existingDocumentsToSync,
      progressToastId,
      results,
      graphOptions,
      changes.listedFileModified
    );

    // Step 2: Update folder names
    await updateFolderNames(
      changes.folderNameUpdates,
      progressToastId,
      results
    );

    // Step 3: Create new folders (including empty) only when there are no new files to upload.
    // If there are new files, addNewDocuments() will create the full folder structure (including empty folders)
    // via folderMetadataMap, and we avoid double-creating upload UI items.
    if (changes.newDocuments.length === 0) {
      await addNewFolders(changes, progressToastId, results);
    }

    // Step 4: Add new documents (also creates folders if folderMetadataMap is provided)
    await addNewDocuments(
      changes,
      progressToastId,
      parentFolderId,
      results,
      graphOptions
    );

    // Step 5: Collect missing/inaccessible cloud documents into the report.
    // Both roles see the sync report dialog; admins/owners get an extra action
    // button there to open the missing-files dialog. Upload-only users see the
    // list read-only.
    for (const missing of changes.missingCloudDocuments) {
      const entry: SyncReportDocumentEntry = {
        document: missing.document,
        folderPath: missing.folderPath,
        ownerName: resolveOwnerName(missing.document.userId),
        status: missing.status,
      };
      if (isAdminOrOwner) {
        results.missingFilesForAdminReview.push(entry);
      } else {
        results.skippedMissingDocuments.push(entry);
      }
    }

    return results;
  };

  /**
   * Builds a summary report string from sync results
   */
  const buildSyncReport = (results: SyncResults): string[] => {
    const parts: string[] = [];

    if (results.existingDocumentsSynced > 0) {
      parts.push(
        `${results.existingDocumentsSynced} fil${
          results.existingDocumentsSynced === 1 ? "" : "er"
        } synkroniseret`
      );
    }

    if (results.existingDocumentsNoUpdate > 0) {
      parts.push(
        `${results.existingDocumentsNoUpdate} fil${
          results.existingDocumentsNoUpdate === 1 ? "" : "er"
        } krævede ingen opdatering`
      );
    }

    if (results.folderNamesUpdated > 0) {
      parts.push(
        `${results.folderNamesUpdated} mappenavn${
          results.folderNamesUpdated === 1 ? "" : "e"
        } opdateret`
      );
    }

    if (results.newFoldersCreated > 0) {
      parts.push(
        `${results.newFoldersCreated} ny${
          results.newFoldersCreated === 1 ? " mappe" : "e mapper"
        } oprettet`
      );
    }

    if (results.newDocumentsAdded > 0) {
      parts.push(
        `${results.newDocumentsAdded} ny${
          results.newDocumentsAdded === 1 ? " fil" : "e filer"
        } tilføjet`
      );
    }

    if (results.existingDocumentsFailed > 0) {
      parts.push(
        `${results.existingDocumentsFailed} fil${
          results.existingDocumentsFailed === 1 ? "" : "er"
        } mislykkedes`
      );
    }

    if (results.errors.length > 0) {
      parts.push(`Fejl: ${results.errors.join(", ")}`);
    }

    return parts;
  };

  /**
   * Checks if sync results contain errors
   */
  const hasErrors = (results: SyncResults): boolean => {
    return results.existingDocumentsFailed > 0 || results.errors.length > 0;
  };

  return {
    executeSyncMutations,
    buildSyncReport,
    hasErrors,
  };
};

export default useSyncMutation;
